import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useCan } from '../../hooks/useCan';

const SECTIONS = [
  { key: 'shows', label: 'Shows', path: '/' },
  { key: 'tools', label: 'Tools', path: '/tools' },
  { key: 'fleet', label: 'Fleet', path: '/fleet' },
  { key: 'scoring', label: 'Scoring', path: '/scoring' },
];

export default function SectionSwitcher({ active }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Tools import data and push it to graphics — production work, so a guest
  // limited to text is not offered them.
  const canProduce = useAuthStore(s => !!s.user?.capabilities?.includes('production:edit'));
  // Fleet is the account's camera boxes: its members only, never guests.
  const canFleet = useCan('fleet:operate');
  // Scoring runs competitions with judges and graphics: production work too.
  const allowed = { tools: canProduce, fleet: canFleet, scoring: canProduce };
  const sections = SECTIONS.filter(s => allowed[s.key] !== false || active === s.key);
  const current = sections.find(s => s.key === active) || sections[0];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 text-gray-400 hover:text-app-fg text-sm transition-colors"
      >
        <span className="text-gray-600">/</span>
        <span>{current.label}</span>
        <svg
          className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 w-40 bg-app-surface border border-app-border rounded-lg shadow-xl z-50 py-1">
          {sections.map(s => (
            <button
              key={s.key}
              onClick={() => {
                setOpen(false);
                if (s.key !== active) navigate(s.path);
              }}
              disabled={s.key === active}
              className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                s.key === active
                  ? 'text-app-fg bg-app-elevated cursor-default'
                  : 'text-gray-400 hover:text-app-fg hover:bg-app-elevated'
              }`}
            >
              {s.key === active
                ? <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                : <span className="w-1.5 h-1.5 flex-shrink-0" />}
              <span>{s.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
